import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiEdit, FiTrash2, FiPlus, FiMapPin, FiPhone, FiMail, FiUsers, FiClock } from 'react-icons/fi';
import api from '../services/api';
import { toast } from 'react-toastify';
import { confirmDelete } from '../utils/swal';

const BranchList = () => {
  const navigate = useNavigate();
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, active, inactive

  useEffect(() => {
    fetchBranches();
  }, [filter]);

  const fetchBranches = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filter === 'active') params.is_active = 'true';
      if (filter === 'inactive') params.is_active = 'false';

      const response = await api.get('/branches', { params });
      setBranches(response.data.data || []);
    } catch (error) {
      console.error('Error fetching branches:', error);
      toast.error('Failed to load branches');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (branch) => {
    const confirmed = await confirmDelete(branch.name, 'branch');
    if (!confirmed) return;

    try {
      await api.delete(`/branches/${branch.id}`);
      toast.success('Branch deleted successfully');
      fetchBranches();
    } catch (error) {
      console.error('Error deleting branch:', error);
      toast.error(error.response?.data?.message || 'Failed to delete branch');
    }
  };

  const toggleStatus = async (id, currentStatus) => {
    try {
      await api.patch(`/branches/${id}/status`, { is_active: !currentStatus });
      toast.success(`Branch ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
      fetchBranches();
    } catch (error) {
      console.error('Error toggling status:', error);
      toast.error('Failed to update branch status');
    }
  };

  const formatTime = (time) => {
    if (!time) return 'N/A';
    return new Date(`2000-01-01T${time}`).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Branches</h1>
          <p className="text-gray-600">Manage gym branches and locations</p>
        </div>
        <button
          onClick={() => navigate('/branches/new')}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          <FiPlus /> Add Branch
        </button>
      </div>

      {/* Filters */}
      <div className="mb-6 flex gap-2">
        <button
          onClick={() => setFilter('all')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            filter === 'all'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          All Branches
        </button>
        <button
          onClick={() => setFilter('active')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            filter === 'active'
              ? 'bg-green-600 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          Active
        </button>
        <button
          onClick={() => setFilter('inactive')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            filter === 'inactive'
              ? 'bg-red-600 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          Inactive
        </button>
      </div>

      {/* Branches Grid */}
      {branches.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <p className="text-gray-500 text-lg">No branches found</p>
          <button
            onClick={() => navigate('/branches/new')}
            className="mt-4 text-blue-600 hover:text-blue-700"
          >
            Create your first branch
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {branches.map((branch) => (
            <div
              key={branch.id}
              className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow"
            >
              {/* Card Header */}
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-800 mb-1">
                      {branch.name}
                    </h3>
                    <span className="text-sm text-gray-500 font-mono">
                      Code: {branch.code}
                    </span>
                  </div>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      branch.is_active
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {branch.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>

              {/* Card Body */}
              <div className="p-6 space-y-3">
                {/* Address */}
                {branch.address && (
                  <div className="flex items-start gap-2 text-sm text-gray-600">
                    <FiMapPin className="mt-1 flex-shrink-0" />
                    <span>
                      {branch.address}
                      {branch.city && `, ${branch.city}`}
                      {branch.state && `, ${branch.state}`}
                      {branch.pincode && ` - ${branch.pincode}`}
                    </span>
                  </div>
                )}

                {/* Phone */}
                {branch.phone && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <FiPhone className="flex-shrink-0" />
                    <span>{branch.phone}</span>
                  </div>
                )}

                {/* Email */}
                {branch.email && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <FiMail className="flex-shrink-0" />
                    <span>{branch.email}</span>
                  </div>
                )}

                {/* Timing */}
                {(branch.opening_time || branch.closing_time) && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <FiClock className="flex-shrink-0" />
                    <span>
                      {formatTime(branch.opening_time)} - {formatTime(branch.closing_time)}
                    </span>
                  </div>
                )}

                {/* Stats */}
                <div className="flex items-center gap-4 pt-3 border-t border-gray-200">
                  <div className="flex items-center gap-2 text-sm">
                    <FiUsers className="text-blue-600" />
                    <span className="font-semibold">{branch.total_members || 0}</span>
                    <span className="text-gray-500">Members</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <FiUsers className="text-green-600" />
                    <span className="font-semibold">{branch.total_trainers || 0}</span>
                    <span className="text-gray-500">Trainers</span>
                  </div>
                </div>

                {/* Manager Info */}
                {branch.manager_name && (
                  <div className="pt-3 border-t border-gray-200">
                    <p className="text-xs text-gray-500">Branch Manager</p>
                    <p className="text-sm font-semibold text-gray-700">
                      {branch.manager_name}
                    </p>
                    {branch.manager_phone && (
                      <p className="text-sm text-gray-600">{branch.manager_phone}</p>
                    )}
                  </div>
                )}
              </div>

              {/* Card Footer */}
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
                <button
                  onClick={() => navigate(`/branches/${branch.id}`)}
                  className="text-blue-600 hover:text-blue-700 font-medium text-sm"
                >
                  View Details
                </button>
                <div className="flex gap-2">
                  <button
                    onClick={() => navigate(`/branches/edit/${branch.id}`)}
                    className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Edit"
                  >
                    <FiEdit size={18} />
                  </button>
                  <button
                    onClick={() => toggleStatus(branch.id, branch.is_active)}
                    className={`px-3 py-1 text-xs rounded-lg transition-colors ${
                      branch.is_active
                        ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                        : 'bg-green-100 text-green-700 hover:bg-green-200'
                    }`}
                  >
                    {branch.is_active ? 'Deactivate' : 'Activate'}
                  </button>
                  <button
                    onClick={() => handleDelete(branch)}
                    className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete"
                  >
                    <FiTrash2 size={18} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BranchList;
